import React, { FC, SyntheticEvent, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useRouter } from 'next/router';
import { useFetchGenerateAgentCode } from '~/src/repositories/user/hooks/use-fetch-generate-agent-code';
import { useOrganizationsIsLoading } from '~/src/hooks/use-organizations-is-loading.hook';
import { selectAgentId, selectUserProfileIsUpdating } from '~/src/redux/user/user.selectors';
import { generateRefLink } from '~/src/components/blocks/agency-products/recommend-product-banner/recommend-product-banner.utils';
import { CLIENTS_REQUEST, SER_CON_AGENTS_URL } from '~/src/params';
import { MainButtonVisualEnum } from '~/src/components/form-components/main-button/main-button-visual.enum';
import { recommendProductBannerContent } from '~/src/components/blocks/agency-products/recommend-product-banner/recommend-product-banner.constants';
import { KnowledgeBaseBannersSkeleton } from '~/src/components/skeletons/knowledge-base-banners-skeleton/knowledge-base-banners-skeleton';
import { onSendButtonClickAnalytics } from './recommend-product-banner.analytics';
import { BannerImages } from '~/src/components/blocks/agency-products/recommend-product-banner/recommend-product-banner-components/banner-images/banner-images';
import {
    KnowledgeBaseSkeletonTypeEnum,
} from '~/src/components/skeletons/knowledge-base-banners-skeleton/knowledge-base-banners-skeleton.types';
import type { ReferralProductInterface } from '~/src/models/types/referral-product.types';
import * as Styled from './recommend-product-banner.styles';

interface RecommendProductBannerProps {
    productData: ReferralProductInterface;
    isAuth: boolean;
    isAuthFinished: boolean;
    isConnectedStatus: boolean;
}

export const RecommendProductBanner: FC<RecommendProductBannerProps> = ({
    productData,
    isAuth,
    isAuthFinished,
    isConnectedStatus,
}) => {
    const router = useRouter();
    const dispatch = useDispatch();
    const userProfileIsUpdating = useSelector(selectUserProfileIsUpdating);
    const { referralUrl } = productData;
    const isShowCreateLead = isAuthFinished && isAuth && isConnectedStatus;
    const [isShowBanner, setIsShowBanner] = useState(false);
    const organizationsIsLoading = useOrganizationsIsLoading();

    const { refetch } = useFetchGenerateAgentCode();
    const agentId = useSelector(selectAgentId);

    useEffect(() => {
        const isNotAuthUser = isAuthFinished && !isAuth;
        const isAuthNotAgent = isAuthFinished && isAuth && !isConnectedStatus;

        if (isShowCreateLead || isAuthNotAgent || isNotAuthUser) {
            setIsShowBanner(true);
        } else {
            setIsShowBanner(false);
        }
    }, [isAuthFinished, isAuth, isConnectedStatus]);

    useEffect(() => {
        agentId.length && referralUrl?.length && generateRefLink(`${referralUrl}${agentId}`, dispatch);
    }, [agentId]);

    const handleClick = async () => {
        onSendButtonClickAnalytics('stat_agentom');
        await router.push({ pathname: `${SER_CON_AGENTS_URL}` });
    };

    const handleCreateNewLead = async () => {
        onSendButtonClickAnalytics('novyi_lid');
        await router.push({ pathname: `${CLIENTS_REQUEST}` });
    };

    const getReferralLink = async (event: SyntheticEvent<HTMLElement, Event>) => {
        event.stopPropagation();
        event.preventDefault();

        onSendButtonClickAnalytics('referalnaya_ssylka');

        if (agentId && referralUrl) {
            generateRefLink(`${referralUrl}${agentId}`, dispatch);
        } else {
            await refetch();
        }
    };

    const renderContent = () => {
        if (!isShowBanner) {
            return <></>;
        }

        if (organizationsIsLoading || userProfileIsUpdating) {
            return (
                <Styled.RecommendProductBannerContainer>
                    <KnowledgeBaseBannersSkeleton
                        type={KnowledgeBaseSkeletonTypeEnum.LOWER_BANNER}
                    />
                </Styled.RecommendProductBannerContainer>
            );
        }

        return (
            <Styled.RecommendProductBannerContainer>
                <BannerImages isReferralUrl={!!referralUrl?.length && isShowCreateLead} />
                <Styled.AboutProductBannerTitle isShowCreateLead={isShowCreateLead}>
                    {isShowCreateLead
                        ? recommendProductBannerContent.title.connectedStatus
                        : recommendProductBannerContent.title.default}
                </Styled.AboutProductBannerTitle>
                {isShowCreateLead ? (
                    <Styled.ButtonGroup>
                        <Styled.NewLeadButton
                            onClick={handleCreateNewLead}
                            textForSSR={recommendProductBannerContent.buttonText.newLead}
                            type={'button'}
                            typeVisual={MainButtonVisualEnum.primary}
                        >
                            {recommendProductBannerContent.buttonText.newLead}
                        </Styled.NewLeadButton>
                        {!!referralUrl?.length && (
                            <Styled.ReferralLinkButton
                                onClick={(e) => getReferralLink(e)}
                                textForSSR={recommendProductBannerContent.buttonText.referralLink}
                                type={'button'}
                                typeVisual={MainButtonVisualEnum.alwaysWhite}
                            >
                                {recommendProductBannerContent.buttonText.referralLink}
                            </Styled.ReferralLinkButton>
                        )}
                    </Styled.ButtonGroup>
                ) : (
                    <Styled.BottomContainer isShowCreateLead={isShowCreateLead}>
                        <Styled.SubText>{recommendProductBannerContent.defaultText}</Styled.SubText>
                        <Styled.BecomeAgentButton
                            onClick={handleClick}
                            textForSSR={recommendProductBannerContent.buttonText.becomeAgent}
                            type={'button'}
                        >
                            {recommendProductBannerContent.buttonText.becomeAgent}
                        </Styled.BecomeAgentButton>
                    </Styled.BottomContainer>
                )}
            </Styled.RecommendProductBannerContainer>
        );
    };

    return <>{renderContent()}</>;
};
